import React, {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback
} from "react";
import SignaturePad from "signature_pad";
import { Eraser } from "lucide-react";

export interface SignaturePadHandle {
  clear: () => void;
  isEmpty: () => boolean;
  toDataURL: (type?: string) => string;
}

interface NdaSignaturePadProps {
  onBegin?: () => void;
  onEnd?: () => void;
  className?: string;
}

/**
 * Wraps the `signature_pad` library in a React component.
 * Exposes clear(), isEmpty(), and toDataURL() via ref.
 * Handles canvas DPI scaling for crisp rendering on HiDPI screens.
 * Supports mouse, touch, and stylus input natively via signature_pad.
 */
const NdaSignaturePad = forwardRef<SignaturePadHandle, NdaSignaturePadProps>(
  ({ onBegin, onEnd, className }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const padRef = useRef<SignaturePad | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isEmpty, setIsEmpty] = React.useState(true);

    const onBeginRef = useRef(onBegin);
    const onEndRef = useRef(onEnd);

    useEffect(() => {
      onBeginRef.current = onBegin;
      onEndRef.current = onEnd;
    });

    // ── Scale canvas for device pixel ratio ──────────────────────────────────
    const scaleCanvas = useCallback(() => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;

      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const w = container.offsetWidth;
      const h = container.offsetHeight;

      canvas.width = w * ratio;
      canvas.height = h * ratio;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;

      const ctx = canvas.getContext("2d");
      if (ctx) ctx.scale(ratio, ratio);

      padRef.current?.clear();
    }, []);

    // ── Initialize signature_pad ─────────────────────────────────────────────
    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const pad = new SignaturePad(canvas, {
        minWidth: 1.5,
        maxWidth: 3.5,
        penColor: "#0f1f5c",
        backgroundColor: "rgba(0,0,0,0)" // transparent
      });

      pad.addEventListener("beginStroke", () => {
        setIsEmpty(false);
        onBeginRef.current?.();
      });
      pad.addEventListener("endStroke", () => {
        onEndRef.current?.();
      });

      padRef.current = pad;
      scaleCanvas();

      // Resize observer to re-scale if container resizes
      const ro = new ResizeObserver(() => scaleCanvas());
      if (containerRef.current) ro.observe(containerRef.current);

      return () => {
        ro.disconnect();
        pad.off();
      };
    }, [scaleCanvas]);

    // ── Expose imperative API ────────────────────────────────────────────────
    useImperativeHandle(ref, () => ({
      clear: () => {
        padRef.current?.clear();
        setIsEmpty(true);
        onEndRef.current?.();
      },
      isEmpty: () => padRef.current?.isEmpty() ?? true,
      toDataURL: (type = "image/png") => padRef.current?.toDataURL(type) ?? ""
    }));

    return (
      <div
        ref={containerRef}
        className={`relative w-full rounded-xl border-2 transition ${
          isEmpty
            ? "border-dashed border-slate-300 bg-slate-50/60"
            : "border-ctb-blue/30 bg-white shadow-inner shadow-ctb-blue/5"
        } ${className ?? ""}`}
        style={{ height: 160, touchAction: "none" }}
      >
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full rounded-xl"
          style={{ touchAction: "none", cursor: "crosshair" }}
        />

        {/* Placeholder text — disappears when user starts drawing */}
        {isEmpty && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none select-none gap-1">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="text-slate-300"
            >
              <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
            </svg>
            <span className="text-[11px] text-slate-400 font-medium">
              Draw your signature here
            </span>
          </div>
        )}

        {/* Clear button — only shown when there's something to clear */}
        {!isEmpty && (
          <button
            type="button"
            onClick={() => {
              padRef.current?.clear();
              setIsEmpty(true);
              onEndRef.current?.();
            }}
            className="absolute top-2 right-2 flex items-center gap-1 px-2.5 py-1 rounded-lg
                       bg-white border border-slate-200 text-[10px] font-semibold text-slate-500
                       hover:text-rose-500 hover:border-rose-200 hover:bg-rose-50
                       shadow-sm transition-all z-10"
            title="Clear signature"
          >
            <Eraser size={10} />
            Clear
          </button>
        )}
      </div>
    );
  }
);

NdaSignaturePad.displayName = "NdaSignaturePad";
export default NdaSignaturePad;
